/**
 * L'installation sur l'écran d'accueil — décision 2026-09-06.
 *
 * ⚠️ `beforeinstallprompt` est tiré TÔT, avant que React ne soit monté.
 * L'écouteur vit donc au niveau du module, importé depuis `main.tsx` : posé
 * plus tard, dans un composant, l'événement serait déjà passé et le bouton
 * n'apparaîtrait jamais — sans la moindre erreur pour le dire.
 *
 * iOS n'a PAS cet événement, et n'en aura pas : Apple impose « Partager → Sur
 * l'écran d'accueil », à la main. D'où un mode `manual` plutôt qu'un bouton
 * qui ne pourrait pas aboutir.
 */

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: InstallPromptEvent | null = null
const listeners = new Set<() => void>()

function notify() {
  for (const listener of listeners) listener()
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Sans ça, Chrome pose sa propre barre en bas de l'écran. On veut le
    // proposer à NOTRE moment — dans le Profil — pas par-dessus une saisie.
    event.preventDefault()
    deferred = event as InstallPromptEvent
    notify()
  })

  // L'invite ne se rejoue jamais une fois l'app installée : le bouton doit
  // disparaître de lui-même, sans attendre un rechargement.
  window.addEventListener('appinstalled', () => {
    deferred = null
    notify()
  })
}

/** Ouverte depuis l'écran d'accueil, pas dans un onglet de navigateur. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const iosStandalone = (navigator as { standalone?: boolean }).standalone === true
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches
}

function isApple(): boolean {
  if (typeof navigator === 'undefined') return false
  // `maxTouchPoints` : depuis iPadOS 13, un iPad se déclare « Macintosh ».
  const ipad = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || ipad
}

export type InstallMode =
  /** Déjà sur l'écran d'accueil : rien à proposer. */
  | 'installed'
  /** Le navigateur nous a donné l'invite : un bouton suffit. */
  | 'prompt'
  /** Pas d'invite : ce sont des gestes à décrire, jamais un bouton. */
  | 'manual'

export function installMode(): InstallMode {
  if (isStandalone()) return 'installed'
  if (deferred) return 'prompt'
  return 'manual'
}

/**
 * Quels gestes décrire, faute d'invite.
 *
 * Chaque navigateur a les siens, et deux d'entre eux ne savent pas installer
 * du tout. Dire « ouvre le menu de ton navigateur » à quelqu'un dont le
 * navigateur n'a pas cette entrée, c'est l'envoyer chercher ce qui n'existe
 * pas — autant nommer le chemin exact, ou dire franchement que c'est
 * impossible ici.
 */
export type ManualHint =
  | 'ios'
  | 'firefox-android'
  | 'firefox-desktop'
  | 'safari-desktop'
  /** Navigateur intégré d'une autre app (Facebook, Messenger…) : aucun accès. */
  | 'in-app'
  /** Chrome ou Edge, mais l'invite n'est pas venue — souvent : déjà installée. */
  | 'chromium'
  | 'unknown'

export function manualHint(): ManualHint {
  if (typeof navigator === 'undefined') return 'unknown'
  const ua = navigator.userAgent

  // En premier : ces navigateurs-là embarquent parfois « Safari » ou
  // « Chrome » dans leur signature, et seraient mal reconnus après coup.
  if (/FBAN|FBAV|FB_IAB|Instagram|Line\/|Twitter/i.test(ua)) return 'in-app'
  if (isApple()) return 'ios'
  if (/Firefox\/|FxiOS/i.test(ua)) return /Android/i.test(ua) ? 'firefox-android' : 'firefox-desktop'
  if (/Edg\/|Chrome\//i.test(ua)) return 'chromium'
  if (/Safari\//i.test(ua)) return 'safari-desktop'
  return 'unknown'
}

/**
 * L'app est-elle DÉJÀ posée, alors qu'on la regarde dans un onglet ?
 *
 * Chrome n'émet pas `beforeinstallprompt` pour une app déjà installée : sans
 * cette question, l'écran conseillerait de l'installer à quelqu'un qui l'a
 * déjà fait. Seuls les navigateurs Chromium savent répondre — ailleurs, on
 * n'en sait rien, et on ne prétend pas le contraire.
 *
 * ⚠️ Exige `related_applications` dans le manifeste (voir `vite.config.ts`) :
 * sans cette déclaration, la réponse est toujours vide.
 */
export async function isInstalledElsewhere(): Promise<boolean> {
  const api = navigator as Navigator & {
    getInstalledRelatedApps?: () => Promise<Array<{ platform?: string }>>
  }
  if (!api.getInstalledRelatedApps) return false
  try {
    const apps = await api.getInstalledRelatedApps()
    return apps.some((app) => app.platform === 'webapp')
  } catch {
    return false
  }
}

/** Ouvre l'invite du navigateur. `true` si l'app a été installée. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false
  await deferred.prompt()
  const { outcome } = await deferred.userChoice
  // Un événement d'invite ne se rejoue pas : gardé, il ferait échouer le
  // deuxième appel en silence.
  deferred = null
  notify()
  return outcome === 'accepted'
}

/** Prévient quand l'invite arrive, ou quand l'app vient d'être installée. */
export function onInstallChange(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}
